import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, useHistory } from "react-router-dom";
import classnames from "classnames";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  TabContent,
  TabPane,
  NavLink,
  Button,
} from "reactstrap";

import { BreadCrumb, NoResultFound } from "../../components/Common";
import {
  deleteGrade,
  deleteGradeConsent,
  getCourseSelectList,
  getGradesDetailByCourse,
} from "../../store/actions";
import { ToastContainer, toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { State, GradeDto } from "../../interfaces";
import { SelectListDto } from "../../models";
import { TableSpinner, Grade } from "../../components";
import { AddEditGradeModal } from "../Grade/AddEditGradeModal";
import DeleteModal from "../../components/Common/DeleteModal";
import { SendPushNotificationModal } from "../NotifyUsers";
// Import Images

type TProps = RouteComponentProps & {};
export const SemestersTabsPage: React.FC<TProps> = () => {
  //#region hooks
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const history = useHistory();
  //#endregion

  //#region internal state
  const [courseId, setCourseId] = useState<string | null>(null);
  const [showAddEditGradeModal, setShowAddEditGradeModal] = useState(false);
  const [showDeleteGradeModal, setShowDeleteGradeModal] = useState(false);
  const [editingGradeId, setEditingGradeId] = useState<string | null>(null);
  const [deletingGradeId, setDeletingGradeId] = useState<string | null>(null);
  const [notifyingGrade, setNotifyingGrade] = useState<GradeDto | null>(null);
  const [activeTab, setActiveTab] = useState<string>();
  const [showSendPushNotificationModal, setShowSendPushNotificationModal] =
    useState(false);
  //#endregion

  //#region redux state

  const {
    response: grades,
    loading: loadingGrades,
    error: loadingGradesError,
  } = useSelector(
    (state: any): State<GradeDto[]> => state.grade.gradesDetailByCourse
  );

  const {
    loading: deleteGradeLoading,
    error: deleteGradeError,
    success: deleteGradeSuccess,
  } = useSelector((state: any): State<GradeDto[]> => state.grade.deleteGrade);

  const { response: courses } = useSelector(
    (state: any): State<SelectListDto[]> => state.course.courseSelectList
  );
  //#endregion

  //#region handlers
  const onAddGrade = () => {
    if (courseId) {
      setShowAddEditGradeModal(true);
    }
  };

  const onEditGrade = (id: string) => {
    if (courseId && id) {
      setEditingGradeId(id);
      setShowAddEditGradeModal(true);
    }
  };

  const onDeleteGrade = (id: string) => {
    setDeletingGradeId(id);
    setShowDeleteGradeModal(true);
    dispatch(deleteGradeConsent(id));
  };

  const onDeleteGradeConfirm = () => {
    dispatch(deleteGrade(deletingGradeId as string, history));
    setDeletingGradeId(null);
  };

  const toggleTab = (changingTab: any) => {
    if (activeTab !== changingTab) {
      setActiveTab(changingTab);
      setCourseId(changingTab);
    }
  };

  const onSendPushNotification = (_grade: GradeDto) => {
    setNotifyingGrade(_grade);
    setShowSendPushNotificationModal(true);
  };

  //#endregion

  //#region effects

  useEffect(() => {
    dispatch(getCourseSelectList());
  }, []);

  useEffect(() => {
    const hash = history.location.hash;
    if (hash && courses && courses.length > 0) {
      let id = hash.replace("#", "");
      setActiveTab(id);
      setCourseId(id);
      dispatch(getGradesDetailByCourse(id));
    } else {
      if (courses && courses.length > 0) {
        setActiveTab(courses[0].id);
        setCourseId(courses[0].id);
        dispatch(getGradesDetailByCourse(courses[0].id));
      }
    }
  }, [courses]);

  useEffect(() => {
    if (courseId) dispatch(getGradesDetailByCourse(courseId));
  }, [courseId]);

  useEffect(() => {
    if (!deleteGradeLoading && deleteGradeSuccess) {
      setDeletingGradeId(null);
      setShowDeleteGradeModal(false);
      if (courseId) {
        dispatch(getGradesDetailByCourse(courseId as string));
      }
    }
  }, [deleteGradeLoading]);

  useEffect(() => {
    if (!loadingGrades && loadingGradesError)
      toast.error(loadingGradesError, {
        autoClose: 3000,
      });
  }, [loadingGrades]);

  //#endregion

  document.title = `${t("documentTitle", { pageName: "semesters" })}`;

  return (
    <React.Fragment>
      <ToastContainer />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("courses")} pageTitle={t("")} />
          <Row>
            <Col xxl={12}>
              <Card>
                <CardBody>
                  <Nav pills className="nav-success">
                    {courses &&
                      courses.length > 0 &&
                      courses?.map((course: SelectListDto, index: number) => (
                        <NavItem key={index}>
                          <NavLink
                            href={`#${course.id}`}
                            style={{ cursor: "pointer" }}
                            className={classnames({
                              active: activeTab === course.id,
                            })}
                            onClick={() => {
                              toggleTab(course.id);
                            }}
                          >
                            {i18n.language === "ar"
                              ? course.value.ar
                              : course.value.en}
                          </NavLink>
                        </NavItem>
                      ))}
                  </Nav>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row className="g-2">
            <Col className="col-sm-auto ms-auto">
              <div className="list-grid-nav hstack gap-1">
                {courseId && (
                  <Button
                    color="success"
                    onClick={() => {
                      onAddGrade();
                    }}
                  >
                    <i className="ri-add-fill me-1 align-bottom"></i>
                    {t("addItem", {
                      item: "grade",
                    })}
                  </Button>
                )}
              </div>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col xxl={12}>
              <TabContent activeTab={activeTab}>
                {courses &&
                  courses.length > 0 &&
                  courses?.map((course: SelectListDto, index: number) => (
                    <TabPane tabId={course.id} id={`${course.id}`} key={index}>
                      <React.Fragment>
                        {courseId && (
                          <CardBody>
                            <Col lg={12}>
                              <Row className="team-list grid-view-filter">
                                {!loadingGrades &&
                                  grades &&
                                  grades.length > 0 &&
                                  grades.map((item: GradeDto, key) => (
                                    <Col key={key}>
                                      <Grade
                                        {...item}
                                        onEditGrade={() => onEditGrade(item.id)}
                                        onDeleteGrade={() =>
                                          onDeleteGrade(item.id)
                                        }
                                        onSendPushNotification={() =>
                                          onSendPushNotification(item)
                                        }
                                      />
                                    </Col>
                                  ))}
                              </Row>
                            </Col>
                          </CardBody>
                        )}
                      </React.Fragment>
                    </TabPane>
                  ))}
              </TabContent>
            </Col>
          </Row>
          {loadingGrades && <TableSpinner />}
          {!loadingGrades && (!grades || grades.length == 0) && (
            <NoResultFound />
          )}
        </Container>
      </div>
      <AddEditGradeModal
        courseId={courseId || ""}
        id={editingGradeId}
        show={showAddEditGradeModal}
        onCloseClick={() => {
          setEditingGradeId(null);
          setShowAddEditGradeModal(false);
          if (courseId) {
            dispatch(getGradesDetailByCourse(courseId));
          }
        }}
      />
      <DeleteModal
        loading={deleteGradeLoading}
        show={showDeleteGradeModal}
        onDeleteClick={() => onDeleteGradeConfirm()}
        onCloseClick={() => {
          setShowDeleteGradeModal(false);
        }}
        error={deleteGradeError}
      />

      {notifyingGrade && (
        <SendPushNotificationModal
          show={showSendPushNotificationModal}
          onCloseClick={() => {
            setShowSendPushNotificationModal(false);
            setNotifyingGrade(null);
          }}
          mode="grade"
          grade={[{ id: notifyingGrade?.id, name: notifyingGrade?.name }]}
        />
      )}
    </React.Fragment>
  );
};
